import React from 'react'
import { withFirebase } from '../../components/Fb'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import AddIcon from '@mui/icons-material/Add'
import moment from 'moment'
import 'moment/locale/fi'
import DayReservationDialog from '../../components/Dialogs/DayReservationDialog'
import {
  bookingsOn,
  dateRangeText,
  formatEuro,
  monthDays,
  shiftMonth,
  unpaidCents,
  usageByDate,
} from '../../components/utils/dayresources'

const WEEKDAYS = ['su', 'ma', 'ti', 'ke', 'to', 'pe', 'la']
const today = () => moment().format('YYYY-MM-DD')

// Päivävaraukset: day resources (päiväresurssit) booked per calendar day, in a month grid -- rows are
// resources, columns the days. Kept apart from the hourly calendars. See docs/features/day-resources.md.
class DayReservationsContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      month: moment().format('YYYY-MM'),
      resources: [],
      bookings: [],
      loading: true,
      error: null,
      dialogOpen: false,
      dialogBooking: null,
      dialogInitial: null,
    }
  }

  componentDidMount() {
    this.unsubscribeResources = this.props.firebase.listenDayResources().onSnapshot(
      (snap) => {
        const resources = snap.docs
          .map((doc) => ({ ...doc.data(), id: doc.id }))
          .filter((r) => r.state !== 'deleted')
          .sort((a, b) => (a.order || 1000) - (b.order || 1000) || (a.displayName || '').localeCompare(b.displayName || ''))
        this.setState({ resources, loading: false })
      },
      (error) => this.setState({ error: error.message, loading: false })
    )
    this.listenMonth()
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.month !== this.state.month) this.listenMonth()
  }

  componentWillUnmount() {
    if (this.unsubscribeResources) this.unsubscribeResources()
    if (this.unsubscribeBookings) this.unsubscribeBookings()
  }

  listenMonth = () => {
    if (this.unsubscribeBookings) this.unsubscribeBookings()
    const days = monthDays(this.state.month)
    this.unsubscribeBookings = this.props.firebase.listenDayReservations(
      days[0],
      days[days.length - 1],
      (bookings) => this.setState({ bookings }),
      (error) => this.setState({ error: error.message })
    )
  }

  openNew = (resourceId, date) => {
    this.setState({ dialogOpen: true, dialogBooking: null, dialogInitial: { resourceId, date } })
  }

  openBooking = (booking) => {
    this.setState({ dialogOpen: true, dialogBooking: booking, dialogInitial: null })
  }

  closeDialog = () => this.setState({ dialogOpen: false })

  // The dialog's booking follows the live list, so payments added in the dialog show up there.
  currentDialogBooking = () => {
    const b = this.state.dialogBooking
    if (!b) return null
    return this.state.bookings.find((x) => x.id === b.id) || b
  }

  renderCell = (resource, date, usage) => {
    const capacity = resource.quantity || 1
    const used = (usage[resource.id] && usage[resource.id][date]) || 0
    const isPast = date < today()
    const covering = bookingsOn(this.state.bookings, resource.id, date)
    let bg = 'transparent'
    if (used >= capacity) bg = 'error.light'
    else if (used > 0) bg = 'warning.light'
    const sx = {
      p: 0.5,
      minWidth: 34,
      textAlign: 'center',
      cursor: 'pointer',
      bgcolor: bg,
      opacity: isPast ? 0.6 : 1,
      borderLeft: '1px solid',
      borderColor: 'divider',
      fontSize: '0.75rem',
      '&:hover': { outline: '2px solid', outlineColor: 'primary.main' },
    }
    // A unique item (capacity 1) that is booked opens its booking; otherwise a click starts a new one.
    if (capacity === 1 && covering.length > 0) {
      const b = covering[0]
      return (
        <Tooltip key={date} title={b.payer ? b.payer.name + ' · ' + dateRangeText(b.startDate, b.endDate) : ''}>
          <TableCell sx={sx} onClick={() => this.openBooking(b)}>
            {b.startDate === date || date.endsWith('-01') ? (b.payer && b.payer.name ? b.payer.name.split(' ')[0] : '•') : ''}
          </TableCell>
        </Tooltip>
      )
    }
    return (
      <Tooltip key={date} title={capacity > 1 ? 'Varattu ' + used + ' / ' + capacity : 'Vapaa'}>
        <TableCell sx={sx} onClick={() => (used < capacity ? this.openNew(resource.id, date) : null)}>
          {capacity > 1 ? used + '/' + capacity : ''}
        </TableCell>
      </Tooltip>
    )
  }

  renderGrid = () => {
    const days = monthDays(this.state.month)
    const usage = usageByDate(this.state.bookings)
    const t = today()
    return (
      <Box sx={{ overflowX: 'auto' }}>
        <Table size='small' sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1, minWidth: 160 }}>Resurssi</TableCell>
              {days.map((d) => {
                const wd = new Date(d + 'T00:00:00Z').getUTCDay()
                return (
                  <TableCell
                    key={d}
                    sx={{
                      p: 0.5,
                      textAlign: 'center',
                      fontSize: '0.7rem',
                      fontWeight: d === t ? 'bold' : 'normal',
                      color: wd === 0 || wd === 6 ? 'error.main' : 'text.primary',
                    }}
                  >
                    {Number(d.slice(8))}
                    <br />
                    {WEEKDAYS[wd]}
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {this.state.resources.map((r) => (
              <TableRow key={r.id}>
                <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                  <Typography variant='body2'>{r.displayName}</Typography>
                  <Typography variant='caption' color='textSecondary'>
                    {(r.quantity || 1) + ' kpl · ' + formatEuro(r.pricePerDay) + ' / pv'}
                  </Typography>
                </TableCell>
                {days.map((d) => this.renderCell(r, d, usage))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    )
  }

  renderList = () => {
    const resourceName = (id) => {
      const r = this.state.resources.find((x) => x.id === id)
      return r ? r.displayName : ''
    }
    const bookings = this.state.bookings
      .slice()
      .sort((a, b) => a.startDate.localeCompare(b.startDate))
    if (bookings.length === 0) {
      return <Typography variant='body2'>Ei varauksia tässä kuussa.</Typography>
    }
    return (
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Päivät</TableCell>
            <TableCell>Resurssi</TableCell>
            <TableCell align='right'>Määrä</TableCell>
            <TableCell>Maksaja</TableCell>
            <TableCell align='right'>Hinta</TableCell>
            <TableCell align='right'>Maksamatta</TableCell>
            <TableCell>Tila</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bookings.map((b) => (
            <TableRow key={b.id} hover sx={{ cursor: 'pointer', opacity: b.state === 'cancelled' ? 0.5 : 1 }} onClick={() => this.openBooking(b)}>
              <TableCell>{dateRangeText(b.startDate, b.endDate)}</TableCell>
              <TableCell>{resourceName(b.resourceId) || (b.resource && b.resource.displayName)}</TableCell>
              <TableCell align='right'>{b.quantity}</TableCell>
              <TableCell>
                {b.payer && b.payer.name}
                {b.payer && b.payer.email ? (
                  <Typography variant='caption' color='textSecondary' component='div'>
                    {b.payer.email}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell align='right'>{formatEuro(b.total)}</TableCell>
              <TableCell align='right'>{formatEuro(unpaidCents(b))}</TableCell>
              <TableCell>
                {b.state === 'cancelled' ? (
                  <Chip size='small' label='Peruttu' />
                ) : unpaidCents(b) === 0 ? (
                  <Chip size='small' color='success' label='Maksettu' />
                ) : (
                  <Chip size='small' color='warning' label='Avoin' />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  }

  render() {
    const { month, resources, loading, error } = this.state
    return (
      <div className='content'>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Typography variant='h5' sx={{ mr: 2 }}>
                Päivävaraukset
              </Typography>
              <IconButton aria-label='Edellinen kuukausi' onClick={() => this.setState({ month: shiftMonth(month, -1) })}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant='h6' sx={{ minWidth: 150, textAlign: 'center', textTransform: 'capitalize' }}>
                {moment(month + '-01').locale('fi').format('MMMM YYYY')}
              </Typography>
              <IconButton aria-label='Seuraava kuukausi' onClick={() => this.setState({ month: shiftMonth(month, 1) })}>
                <ChevronRightIcon />
              </IconButton>
              <Button size='small' onClick={() => this.setState({ month: moment().format('YYYY-MM') })}>
                Tänään
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant='contained'
                startIcon={<AddIcon />}
                disabled={resources.length === 0}
                onClick={() => this.openNew(resources[0] ? resources[0].id : '', today())}
              >
                Uusi varaus
              </Button>
            </Box>
            {error && (
              <Alert severity='error' sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {!loading && resources.length === 0 ? (
              <Alert severity='info'>Ei päiväresursseja. Lisää ne Resurssit-sivun Päiväresurssit-välilehdellä.</Alert>
            ) : (
              this.renderGrid()
            )}
            <Typography variant='caption' color='textSecondary' component='p' sx={{ mt: 1 }}>
              Valitse vapaa päivä aloittaaksesi varauksen. Keltainen = osittain varattu, punainen = täynnä. Varaukset laskutetaan
              Laskutettavaa-sivulta.
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant='h6' sx={{ mb: 1 }}>
              Kuukauden varaukset
            </Typography>
            {this.renderList()}
          </CardContent>
        </Card>
        <DayReservationDialog
          open={this.state.dialogOpen}
          onClose={this.closeDialog}
          resources={resources}
          booking={this.currentDialogBooking()}
          initial={this.state.dialogInitial}
        />
      </div>
    )
  }
}

export default withFirebase(DayReservationsContainer)
